import AuthService from './authService';
import { toast } from 'react-toastify';

class DiscountService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
    //return process.env.REACT_APP_API_URL || 'https://localhost:44330';
  }

  static parseError = async (response) => {
    const fallback = { message: `Request failed with status ${response.status}` };
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      return fallback;
    }

    try {
      return await response.json();
    } catch {
      return fallback;
    }
  };

  // Calculate discount amounts (preview, no save)
  static calculateDiscount = async (discountData) => {
    try {
      const requestBody = {
        CreditId: discountData.creditId,
        SellerId: discountData.sellerId,
        BuyerId: discountData.buyerId,
        ReceivableAmount: discountData.receivableAmount,
        DueDate: discountData.dueDate,
        TermDays: discountData.termDays,
        AnnualInterestRate: discountData.annualInterestRate || null,
        CommissionRate: discountData.commissionRate || null,
      };

      // IBAN is optional for calculation, add if available
      if (discountData.sellerIban) {
        requestBody.SellerIban = discountData.sellerIban;
      }

      const response = await fetch(`${this.apiBase}/api/Discount/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data,
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Calculation failed. Please check your input and try again.',
        };
      }
    } catch (error) {
      console.error('Error calculating discount:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Create a new discount request
  static createDiscount = async (discountData) => {
    try {
      const requestBody = {
        CreditId: discountData.creditId,
        SellerId: discountData.sellerId,
        BuyerId: discountData.buyerId,
        ReceivableAmount: discountData.receivableAmount,
        DueDate: discountData.dueDate,
        TermDays: discountData.termDays,
        AnnualInterestRate: discountData.annualInterestRate || null,
        CommissionRate: discountData.commissionRate || null,
      };

      // IBAN ve ContractAccepted zorunlu alanlar - her zaman gönder
      requestBody.SellerIban = discountData.sellerIban || '';
      requestBody.ContractAccepted = discountData.contractAccepted || false;

      const response = await fetch(`${this.apiBase}/api/Discount/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data,
          message: result.message,
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Failed to create discount request. Please try again.');
        return {
          success: false,
          message: error.message || 'Failed to create discount request. Please try again.',
        };
      }
    } catch (error) {
      console.error('Error creating discount:', error);
      toast.error('An error occurred: ' + error.message);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Get discount by ID
  static getDiscountById = async (discountId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/${discountId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data,
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'İskonto bulunamadı',
        };
      }
    } catch (error) {
      console.error('Error getting discount:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Get discounts by seller ID
  static getDiscountsBySeller = async (sellerId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getBySeller/${sellerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || [],
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Discount list could not be retrieved',
          data: [],
        };
      }
    } catch (error) {
      console.error('Error getting discounts by seller:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
        data: [],
      };
    }
  };

  // Get discounts by buyer ID
  static getDiscountsByBuyer = async (buyerId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getByBuyer/${buyerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || [],
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Discount list could not be retrieved',
          data: [],
        };
      }
    } catch (error) {
      console.error('Error getting discounts by buyer:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
        data: [],
      };
    }
  };

  // Get pending discounts (Admin)
  static getPendingDiscounts = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getPending`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || [],
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Pending discount list could not be retrieved',
          data: [],
        };
      }
    } catch (error) {
      console.error('Error getting pending discounts:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
        data: [],
      };
    }
  };

  // Get all discounts (Admin)
  static getAllDiscounts = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getAll`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || [],
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Discount list could not be retrieved',
          data: [],
        };
      }
    } catch (error) {
      console.error('Error getting all discounts:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
        data: [],
      };
    }
  };

  // Get discounts by status (Admin)
  static getDiscountsByStatus = async (status) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getByStatus?status=${status}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || [],
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Discount list could not be retrieved',
          data: [],
        };
      }
    } catch (error) {
      console.error('Error getting discounts by status:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
        data: [],
      };
    }
  };

  // Approve discount (Admin)
  static approveDiscount = async (discountId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/approve/${discountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'İskonto onaylandı');
        return {
          success: true,
          message: result.message,
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'İskonto onaylanamadı');
        return {
          success: false,
          message: error.message || 'İskonto onaylanamadı',
        };
      }
    } catch (error) {
      console.error('Error approving discount:', error);
      toast.error('Bir hata oluştu: ' + error.message);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Reject discount (Admin)
  static rejectDiscount = async (discountId, reason) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/reject/${discountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify({ Reason: reason }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'İskonto reddedildi');
        return {
          success: true,
          message: result.message,
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'İskonto reddedilemedi');
        return {
          success: false,
          message: error.message || 'İskonto reddedilemedi',
        };
      }
    } catch (error) {
      console.error('Error rejecting discount:', error);
      toast.error('Bir hata oluştu: ' + error.message);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Sign contract by bank (Admin)
  static signContractByBank = async (discountId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/signContractByBank/${discountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Sözleşme banka tarafından imzalandı');
        return {
          success: true,
          message: result.message,
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Sözleşme imzalanamadı');
        return {
          success: false,
          message: error.message || 'Sözleşme imzalanamadı',
        };
      }
    } catch (error) {
      console.error('Error signing contract by bank:', error);
      toast.error('Bir hata oluştu: ' + error.message);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };


  // Get contract by discount ID
  static getContract = async (discountId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/getContract/${discountId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data,
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Sözleşme bulunamadı',
        };
      }
    } catch (error) {
      console.error('Error getting contract:', error);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };

  // Cancel discount
  static cancelDiscount = async (discountId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/Discount/cancel/${discountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'İskonto iptal edildi');
        return {
          success: true,
          message: result.message,
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'İskonto iptal edilemedi');
        return {
          success: false,
          message: error.message || 'İskonto iptal edilemedi',
        };
      }
    } catch (error) {
      console.error('Error cancelling discount:', error);
      toast.error('Bir hata oluştu: ' + error.message);
      return {
        success: false,
          message: error.message || 'An error occurred',
      };
    }
  };
}

export default DiscountService;

